const PHONE_PATTERN = /^[0-9+\-\s]+$/;
const EMAIL_PATTERN =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PINCODE_PATTERN = /^\d{6}$/;
const AADHAAR_PATTERN = /^\d{12}$/;

const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  String(value).trim() === '';

const trimValue = (value) =>
  String(value ?? '').trim();

export const normalizePhone = (
  value,
) =>
  String(value ?? '').replace(
    /\D/g,
    '',
  );

export const validatePhone = (
  value,
  {
    required = false,
    label = 'Phone number',
  } = {},
) => {
  const normalizedValue =
    trimValue(value);

  if (!normalizedValue) {
    return required
      ? `${label} is required.`
      : true;
  }

  if (
    !PHONE_PATTERN.test(
      normalizedValue,
    )
  ) {
    return `${label} can contain only digits, spaces, + and -.`;
  }

  const digitCount =
    normalizePhone(normalizedValue)
      .length;

  if (
    digitCount < 10 ||
    digitCount > 15
  ) {
    return `${label} must contain between 10 and 15 digits.`;
  }

  return true;
};

export const validateEmail = (
  value,
  {
    required = false,
    label = 'Email address',
  } = {},
) => {
  const normalizedValue =
    trimValue(value);

  if (!normalizedValue) {
    return required
      ? `${label} is required.`
      : true;
  }

  if (
    !EMAIL_PATTERN.test(
      normalizedValue,
    )
  ) {
    return `Enter a valid ${label.toLowerCase()}.`;
  }

  return true;
};

export const validatePincode = (
  value,
  {
    required = false,
  } = {},
) => {
  const normalizedValue =
    trimValue(value);

  if (!normalizedValue) {
    return required
      ? 'Pincode is required.'
      : true;
  }

  if (
    !PINCODE_PATTERN.test(
      normalizedValue,
    )
  ) {
    return 'Pincode must contain exactly 6 digits.';
  }

  return true;
};

export const validateAadhaar = (
  value,
) => {
  const normalizedValue =
    String(value ?? '').replace(
      /\s/g,
      '',
    );

  if (!normalizedValue) {
    return true;
  }

  if (
    !AADHAAR_PATTERN.test(
      normalizedValue,
    )
  ) {
    return 'Aadhaar number must contain exactly 12 digits.';
  }

  return true;
};

export const validateNotFutureDate = (
  value,
  label,
) => {
  if (!value) {
    return true;
  }

  const selectedDate =
    new Date(`${value}T00:00:00`);

  const today = new Date();

  today.setHours(
    0,
    0,
    0,
    0,
  );

  if (
    Number.isNaN(
      selectedDate.getTime(),
    )
  ) {
    return `Enter a valid ${label.toLowerCase()}.`;
  }

  if (selectedDate > today) {
    return `${label} cannot be in the future.`;
  }

  return true;
};

const addError = (
  errors,
  path,
  message,
) => {
  if (
    message &&
    message !== true &&
    !errors[path]
  ) {
    errors[path] = message;
  }
};

const getGuardianPairs = (
  parentDetail = {},
) => [
  {
    name:
      parentDetail.father_name,
    phone:
      parentDetail.father_phone,
    namePath:
      'parent_detail.father_name',
    phonePath:
      'parent_detail.father_phone',
    label:
      "Father's",
  },
  {
    name:
      parentDetail.mother_name,
    phone:
      parentDetail.mother_phone,
    namePath:
      'parent_detail.mother_name',
    phonePath:
      'parent_detail.mother_phone',
    label:
      "Mother's",
  },
  {
    name:
      parentDetail.primary_contact_name,
    phone:
      parentDetail.primary_contact_phone,
    namePath:
      'parent_detail.primary_contact_name',
    phonePath:
      'parent_detail.primary_contact_phone',
    label:
      'Primary contact',
  },
];

export const hasGuardianContact = (
  parentDetail = {},
) =>
  getGuardianPairs(
    parentDetail,
  ).some(
    ({ name, phone }) =>
      !isEmpty(name) &&
      !isEmpty(phone),
  );

const validateGuardianDetails = (
  values,
  errors,
) => {
  const parentDetail =
    values.parent_detail || {};

  const guardianPairs =
    getGuardianPairs(parentDetail);

  guardianPairs.forEach(
    ({
      name,
      phone,
      namePath,
      phonePath,
      label,
    }) => {
      if (
        !isEmpty(name) &&
        isEmpty(phone)
      ) {
        addError(
          errors,
          phonePath,
          `${label} phone number is required when the name is entered.`,
        );
      }

      if (
        isEmpty(name) &&
        !isEmpty(phone)
      ) {
        addError(
          errors,
          namePath,
          `${label} name is required when the phone number is entered.`,
        );
      }

      if (!isEmpty(phone)) {
        addError(
          errors,
          phonePath,
          validatePhone(phone, {
            label: `${label} phone number`,
          }),
        );
      }
    },
  );

  if (
    !hasGuardianContact(
      parentDetail,
    )
  ) {
    addError(
      errors,
      'parent_detail.root',
      'Provide at least one complete guardian name and phone number.',
    );
  }

  addError(
    errors,
    'parent_detail.father_email',
    validateEmail(
      parentDetail.father_email,
      {
        label:
          "Father's email address",
      },
    ),
  );

  addError(
    errors,
    'parent_detail.mother_email',
    validateEmail(
      parentDetail.mother_email,
      {
        label:
          "Mother's email address",
      },
    ),
  );
};

const validateCurrentAddress = (
  values,
  errors,
) => {
  const addresses = Array.isArray(
    values.addresses,
  )
    ? values.addresses
    : [];

  const currentAddressIndex =
    addresses.findIndex(
      (address) =>
        address?.address_type ===
        'CURRENT',
    );

  const addressIndex =
    currentAddressIndex >= 0
      ? currentAddressIndex
      : 0;

  const currentAddress =
    addresses[addressIndex] || {};

  if (
    currentAddressIndex < 0
  ) {
    addError(
      errors,
      `addresses.${addressIndex}.address_type`,
      'Current address is required.',
    );
  }

  const requiredAddressFields = [
    {
      key: 'address_line1',
      label: 'Address line 1',
    },
    {
      key: 'city',
      label: 'City',
    },
    {
      key: 'state',
      label: 'State',
    },
    {
      key: 'country',
      label: 'Country',
    },
    {
      key: 'pincode',
      label: 'Pincode',
    },
  ];

  requiredAddressFields.forEach(
    ({ key, label }) => {
      if (
        isEmpty(
          currentAddress[key],
        )
      ) {
        addError(
          errors,
          `addresses.${addressIndex}.${key}`,
          `${label} is required.`,
        );
      }
    },
  );

  addError(
    errors,
    `addresses.${addressIndex}.pincode`,
    validatePincode(
      currentAddress.pincode,
      {
        required: true,
      },
    ),
  );
};

const validateAcademicSelection = (
  values,
  errors,
  {
    classLevels = [],
    sections = [],
  } = {},
) => {
  const academicProfile =
    values.academic_profile || {};

  const academicYearId =
    academicProfile.academic_year;

  const classLevelId =
    academicProfile.class_level;

  const sectionId =
    academicProfile.section;

  if (isEmpty(academicYearId)) {
    addError(
      errors,
      'academic_profile.academic_year',
      'Academic year is required.',
    );
  }

  if (isEmpty(classLevelId)) {
    addError(
      errors,
      'academic_profile.class_level',
      'Class is required.',
    );
  }

  if (isEmpty(sectionId)) {
    addError(
      errors,
      'academic_profile.section',
      'Section is required.',
    );
  }

  if (
    academicYearId &&
    classLevelId &&
    Array.isArray(classLevels) &&
    classLevels.length > 0
  ) {
    const selectedClass =
      classLevels.find(
        (classLevel) =>
          String(classLevel.id) ===
          String(classLevelId),
      );

    const classYearId =
      selectedClass?.academic_year_id ||
      selectedClass?.academic_year?.id ||
      selectedClass?.academic_year;

    if (
      selectedClass &&
      classYearId &&
      String(classYearId) !==
        String(academicYearId)
    ) {
      addError(
        errors,
        'academic_profile.class_level',
        'Selected class does not belong to the selected academic year.',
      );
    }
  }

  if (
    classLevelId &&
    sectionId &&
    Array.isArray(sections) &&
    sections.length > 0
  ) {
    const selectedSection =
      sections.find(
        (section) =>
          String(section.id) ===
          String(sectionId),
      );

    const sectionClassId =
      selectedSection?.class_level_id ||
      selectedSection?.class_level?.id ||
      selectedSection?.class_level;

    if (
      selectedSection &&
      sectionClassId &&
      String(sectionClassId) !==
        String(classLevelId)
    ) {
      addError(
        errors,
        'academic_profile.section',
        'Selected section does not belong to the selected class.',
      );
    }
  }
};

export const validateStudentForm = (
  values = {},
  context = {},
) => {
  const errors = {};

  if (isEmpty(values.first_name)) {
    addError(
      errors,
      'first_name',
      'First name is required.',
    );
  }

  if (isEmpty(values.gender)) {
    addError(
      errors,
      'gender',
      'Gender is required.',
    );
  }

  if (
    isEmpty(values.date_of_birth)
  ) {
    addError(
      errors,
      'date_of_birth',
      'Date of birth is required.',
    );
  } else {
    addError(
      errors,
      'date_of_birth',
      validateNotFutureDate(
        values.date_of_birth,
        'Date of birth',
      ),
    );
  }

  if (
    isEmpty(values.admission_date)
  ) {
    addError(
      errors,
      'admission_date',
      'Admission date is required.',
    );
  } else {
    addError(
      errors,
      'admission_date',
      validateNotFutureDate(
        values.admission_date,
        'Admission date',
      ),
    );
  }

  if (
    values.date_of_birth &&
    values.admission_date
  ) {
    const dateOfBirth = new Date(
      `${values.date_of_birth}T00:00:00`,
    );

    const admissionDate = new Date(
      `${values.admission_date}T00:00:00`,
    );

    if (
      !Number.isNaN(
        dateOfBirth.getTime(),
      ) &&
      !Number.isNaN(
        admissionDate.getTime(),
      ) &&
      admissionDate < dateOfBirth
    ) {
      addError(
        errors,
        'admission_date',
        'Admission date cannot be before the date of birth.',
      );
    }
  }

  if (isEmpty(values.status)) {
    addError(
      errors,
      'status',
      'Student status is required.',
    );
  }

  addError(
    errors,
    'aadhaar_no',
    validateAadhaar(
      values.aadhaar_no,
    ),
  );

  validateAcademicSelection(
    values,
    errors,
    context,
  );

  validateGuardianDetails(
    values,
    errors,
  );

  validateCurrentAddress(
    values,
    errors,
  );

  addError(
    errors,
    'medical_detail.doctor_phone',
    validatePhone(
      values.medical_detail
        ?.doctor_phone,
      {
        label:
          'Doctor phone number',
      },
    ),
  );

  addError(
    errors,
    'previous_school_detail.last_attended_date',
    validateNotFutureDate(
      values.previous_school_detail
        ?.last_attended_date,
      'Last attended date',
    ),
  );

  return errors;
};

export const applyValidationErrors = (
  validationErrors,
  setError,
) => {
  Object.entries(
    validationErrors || {},
  ).forEach(
    ([fieldPath, message]) => {
      setError(
        fieldPath,
        {
          type: 'manual',
          message,
        },
        {
          shouldFocus: false,
        },
      );
    },
  );
};

export const validationErrorsToSummary = (
  validationErrors,
) =>
  Object.values(
    validationErrors || {},
  ).filter(Boolean);

export const getFirstValidationPath = (
  validationErrors,
) =>
  Object.keys(
    validationErrors || {},
  )[0] || '';

export const studentValidationRules = {
  phone: (
    label = 'Phone number',
  ) => ({
    validate: (value) =>
      validatePhone(value, {
        label,
      }),
  }),

  email: (
    label = 'Email address',
  ) => ({
    validate: (value) =>
      validateEmail(value, {
        label,
      }),
  }),

  pincode: {
    validate: (value) =>
      validatePincode(value, {
        required: true,
      }),
  },

  aadhaar: {
    validate: validateAadhaar,
  },
};

export default validateStudentForm;